import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { AgendaPage } from "../agenda/agenda.page.tsx";
import { DashboardPage } from "../dashboard/dashboard.page.tsx";
import { UserModulePage } from "../dashboard/user-module.page.tsx";
import { FirstAppointmentPage } from "../first-appointment/first-appointment.page.tsx";
import { FirstContactPage } from "../first-contact/first-contact.page.tsx";
import { MessagesPage } from "../messages/messages.page.tsx";
import { PatientModulePage } from "../patient/patient-module.page.tsx";
import { PatientsPage } from "../patients/patients.page.tsx";
import { PatientsTodayPage } from "../patients-today/patients-today.page.tsx";
import {
  getCurrentPracticeId,
  getPracticeByUuid,
  setCurrentPracticeId,
} from "./services/practices.service.ts";
import { isPracticeUuid, practiceBase } from "./services/navigation.service.ts";
import { AppShell } from "./shell/app-shell.component.tsx";

const rootRoute = createRootRoute({
  component: Outlet,
});

const siteRootRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({
      to: "/pro/$practiceUuid",
      params: { practiceUuid: getCurrentPracticeId() },
    });
  },
});

const proRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pro",
  beforeLoad: () => {
    throw redirect({
      to: "/pro/$practiceUuid",
      params: { practiceUuid: getCurrentPracticeId() },
    });
  },
});

const practiceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pro/$practiceUuid",
  beforeLoad: ({ params, location }) => {
    const { practiceUuid } = params;

    if (!isPracticeUuid(practiceUuid)) {
      const legacyTail = location.pathname.slice(`/pro/${practiceUuid}`.length);
      throw redirect({
        to: `${practiceBase(getCurrentPracticeId())}${legacyTail || ""}`,
      });
    }

    if (!getPracticeByUuid(practiceUuid)) {
      throw redirect({
        to: "/pro/$practiceUuid",
        params: { practiceUuid: getCurrentPracticeId() },
      });
    }

    setCurrentPracticeId(practiceUuid);
  },
  component: AppShell,
});

const indexRoute = createRoute({
  getParentRoute: () => practiceRoute,
  path: "/",
  component: DashboardPage,
});

const agendaRoute = createRoute({
  getParentRoute: () => practiceRoute,
  path: "/agenda",
  component: AgendaPage,
});

const todayRoute = createRoute({
  getParentRoute: () => practiceRoute,
  path: "/today",
  component: PatientsTodayPage,
});

const messagesRoute = createRoute({
  getParentRoute: () => practiceRoute,
  path: "/messages",
  validateSearch: (
    search: Record<string, unknown>,
  ): { conversation?: number } => {
    const raw = search.conversation;
    const id =
      typeof raw === "number" ? raw : Number.parseInt(String(raw ?? ""), 10);
    return Number.isFinite(id) && id > 0 ? { conversation: id } : {};
  },
  component: MessagesPage,
});

const patientsRoute = createRoute({
  getParentRoute: () => practiceRoute,
  path: "/patients",
  component: PatientsPage,
});

const firstContactRoute = createRoute({
  getParentRoute: () => practiceRoute,
  path: "/first-contact",
  component: FirstContactPage,
});

const firstAppointmentRoute = createRoute({
  getParentRoute: () => practiceRoute,
  path: "/first-appointment",
  component: FirstAppointmentPage,
});

const patientModuleRoute = createRoute({
  getParentRoute: () => practiceRoute,
  path: "/patients/$patientUuid/$module",
  component: PatientModulePage,
});

const userModuleRoute = createRoute({
  getParentRoute: () => practiceRoute,
  path: "/m/$userModuleId",
  component: UserModulePage,
});

const routeTree = rootRoute.addChildren([
  siteRootRedirectRoute,
  proRedirectRoute,
  practiceRoute.addChildren([
    indexRoute,
    agendaRoute,
    todayRoute,
    messagesRoute,
    patientsRoute,
    firstContactRoute,
    firstAppointmentRoute,
    patientModuleRoute,
    userModuleRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
